/**
 * useDailyViral — React Query hooks dla Daily Viral / Inspiracje (Organic).
 * apiRequest zwraca już sparsowany JSON (patrz lib/queryClient) — bez .json().
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { UserContentProfile, ViralInspiration } from "@shared/schema";
import type { HandlePlatform } from "@shared/daily-viral";

export type ProfileInput = {
  niche: string;
  nicheTags?: string[];
  contentPillars?: string[];
  tone?: string;
  offer?: string;
  markets: string[];
  sourceHandle?: string;
  sourcePlatform?: HandlePlatform;
};

export type FeedFilters = {
  niche?: string;
  market?: string;
  platform?: string;
};

export type FeedResponse = {
  profile: UserContentProfile | null;
  items: ViralInspiration[];
};

const PROFILE_KEY = ["daily-viral", "profile"] as const;
const FEED_KEY = ["daily-viral", "feed"] as const;

export function useDailyViralProfile() {
  return useQuery({
    queryKey: PROFILE_KEY,
    queryFn: async (): Promise<UserContentProfile | null> => {
      const data = (await apiRequest("GET", "/api/daily-viral/profile")) as {
        profile: UserContentProfile | null;
      };
      return data.profile;
    },
  });
}

export function useDailyViralFeed(filters: FeedFilters) {
  const params = new URLSearchParams();
  if (filters.niche) params.set("niche", filters.niche);
  if (filters.market) params.set("market", filters.market);
  if (filters.platform) params.set("platform", filters.platform);
  const qs = params.toString();
  return useQuery({
    queryKey: [...FEED_KEY, filters],
    queryFn: async (): Promise<FeedResponse> =>
      (await apiRequest("GET", `/api/daily-viral/feed${qs ? `?${qs}` : ""}`)) as FeedResponse,
  });
}

export function useSaveDailyViralProfile() {
  return useMutation({
    mutationFn: async (input: ProfileInput): Promise<UserContentProfile> => {
      const data = (await apiRequest("PUT", "/api/daily-viral/profile", input)) as {
        profile: UserContentProfile;
      };
      return data.profile;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
      void queryClient.invalidateQueries({ queryKey: FEED_KEY });
    },
  });
}
